// =============================================================================
// calendarCacheService
// =============================================================================
// PURPOSE: Pure localStorage caching for calendar data (no React dependencies).
//
// REQUIREMENTS:
// - TTL-based expiration: events 5min, enrichment 3min
// - Org-scoped cache keys to prevent cross-tenant data leaks
// - Auto-cleanup when localStorage is full (removes oldest half)
//
// ARCHITECTURE:
// - Follows dmCacheService.ts pattern exactly
// - Each cache entry stores: data, timestamp, orgId
// - getCache checks: TTL not expired, orgId matches
// - Silent failures: cache errors should not break the UI
// =============================================================================

import { logger } from "./logger";

import { CACHE_VERSION } from "./cacheVersion";

// Cache configuration
const CACHE_PREFIX = `altara_${CACHE_VERSION}_cal_`;
export const EVENTS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes — live ranges
export const ENRICHMENT_CACHE_TTL = 3 * 60 * 1000; // 3 minutes — live ranges

// Historical tiers (P0.5-CACHE, Jun 13 2026). A range whose END instant is in
// the past can't grow new bookings/cancels, so month-hopping into past months
// shouldn't refetch every TTL (sibling pattern: setterPerformanceCacheService
// getEffectiveTTL). Events get the full 24h. Enrichment is DELIBERATELY capped
// at 1h, not 24h: outcomes for past bookings ARE late-filed (/cleanup-outcomes
// flow), and the enrichmentRefreshKey bypass (realtime INSERT + visibilitychange
// + 30s poll) only covers an OPEN tab — a fresh mount on a historical range
// serves the cache with no bump, so a 24h tier could show a false "Missing
// Outcome" flag for a day. 1h bounds that staleness while still killing the
// month-hop refetch spam. Do not raise it without closing the fresh-mount gap.
const EVENTS_HISTORICAL_TTL = 24 * 60 * 60 * 1000; // 24 hours
const ENRICHMENT_HISTORICAL_TTL = 60 * 60 * 1000; // 1 hour

/**
 * Key format is `events_{orgId}_{startISO}_{endISO}` or
 * `enrich_v5_{orgId}_{startISO}_{endISO}` — ISO instants contain no
 * underscores, so the last `_`-segment is always the range-end instant.
 * Range fully in the past → historical tier.
 */
const getEffectiveTTL = (baseTTL: number, key: string): number => {
  const segments = key.split("_");
  const endMs = Date.parse(segments[segments.length - 1]);
  if (!Number.isFinite(endMs) || endMs >= Date.now()) return baseTTL; // live (or unparseable) range
  const historicalTTL = key.startsWith("enrich")
    ? ENRICHMENT_HISTORICAL_TTL
    : EVENTS_HISTORICAL_TTL;
  return Math.max(baseTTL, historicalTTL);
};

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  orgId: string;
}

/**
 * A reload (Cmd/Ctrl+Shift+R or a plain refresh, the browser does not tell
 * them apart) must always show fresh calendar data. localStorage survives a
 * hard reload, so without this a reload inside the TTL served the pre-reload
 * list (5 min for live weeks, 24h for past ones). Rule: on a reload, every
 * entry written BEFORE this page load is a miss. Entries written after it
 * (this load's own fetches and prefetches) cache normally.
 */
const RELOAD_CUTOFF_MS: number = (() => {
  try {
    const nav = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    return nav?.type === "reload" ? performance.timeOrigin : 0;
  } catch {
    return 0;
  }
})();

/**
 * Get cached data if valid (not expired, same org, not from before a reload).
 * TTL is tiered: the caller passes the LIVE-range TTL; fully-past ranges are
 * automatically extended to the historical tier (see getEffectiveTTL).
 */
export const getCache = <T>(key: string, ttl: number, orgId: string): T | null =>
  getCacheEntry<T>(key, ttl, orgId)?.data ?? null;

/**
 * Same validity rules as getCache, plus WHEN the entry was written, so a
 * caller can paint the cached data and still decide to revalidate it.
 */
export const getCacheEntry = <T>(
  key: string,
  ttl: number,
  orgId: string
): { data: T; timestamp: number } | null => {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;

    const entry: CacheEntry<T> = JSON.parse(raw);
    const isExpired = Date.now() - entry.timestamp > getEffectiveTTL(ttl, key);
    const isSameOrg = entry.orgId === orgId;
    const isPreReload = entry.timestamp < RELOAD_CUTOFF_MS;

    if (isExpired || !isSameOrg || isPreReload) {
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }

    return { data: entry.data, timestamp: entry.timestamp };
  } catch {
    return null;
  }
};

/**
 * Store data in cache with org scope
 */
export const setCache = <T>(key: string, data: T, orgId: string): void => {
  try {
    const entry: CacheEntry<T> = { data, timestamp: Date.now(), orgId };
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // localStorage might be full - clear old caches
    clearOldCaches();
    logger.warn("Calendar cache write failed, cleared old entries");
  }
};

/**
 * Remove oldest half of calendar caches to free up space.
 * Internal use only — called when localStorage is full.
 */
const clearOldCaches = (): void => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    // Remove oldest half of cached items
    keysToRemove
      .slice(0, Math.ceil(keysToRemove.length / 2))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // Ignore cleanup errors
  }
};

/**
 * Generate cache key for raw calendar events
 */
export const getEventsCacheKey = (orgId: string, startISO: string, endISO: string): string => {
  // Prefix bumped to v2 (Sep 10, 2026, P0-SAMESLOT-HIDE) when deduplicateEvents
  // switched its cancel→booking drop from lead+slot to call_id. A warm pre-fix
  // events cache still hides the live booking on a same-slot rebook (Gavryll
  // Singh 16:40), so this surgical bump forces one fresh fetch through the new
  // dedup. Calendar-only — NOT a global MANUAL_SCHEMA_VERSION bump. Old
  // `events_*` (unversioned) entries are never looked up again; they expire.
  return `events_v2_${orgId}_${startISO}_${endISO}`;
};

/**
 * Generate cache key for enrichment data (pre-calls + outcomes)
 */
export const getEnrichmentCacheKey = (orgId: string, startISO: string, endISO: string): string => {
  // Prefix bumped to v2 (May 1, 2026) when CachedEnrichment switched from
  // email-string keys to lead_id UUID keys. Bumped to v3 (Jun 14, 2026) when
  // computeOutcomeBindings began superseding a pending_close binding with a
  // later terminal outcome: old v2 blobs hold the pre-fix (wrong) binding under
  // the SAME key/shape, so they can't self-invalidate — the prefix bump forces
  // a one-time enrichment recompute without a global CACHE_VERSION eviction.
  // Bumped to v4 (Jul 18, 2026) when CachedEnrichment added preCallSetterIdsByLead
  // + preCallSetterNames for the Setters & Dialers filter (new shape, same key).
  // Bumped to v5 (Jul 21, 2026) when computeOutcomeBindings began SKIPPING
  // `pending_superseded` rows: v4 blobs hold the pre-fix binding (booking bound
  // to the dead label instead of the real terminal outcome) under the SAME
  // key/shape, so they cannot self-invalidate. Same surgical-prefix reasoning as
  // the v3 bump — one forced recompute, no global CACHE_VERSION eviction.
  // Bumped to v6 (Aug 10, 2026) when the pre-call fetch in useCalendarEnrichment
  // stopped being date-windowed and became lead-id batched. "Did this lead file a
  // pre-call" is a lead-level fact; bounding it to [rangeStart-7d, rangeEnd] made
  // the answer depend on the week being viewed, so a lead rescheduled into a
  // different week read "No pre-call submitted" over a pre-call that exists (a
  // pre-call's call_start_time is midnight of the ORIGINAL call date and is never
  // restamped when the call moves). v5 blobs hold those false `has_pre_call:false`
  // values under the SAME key/shape, so they cannot self-invalidate.
  // Old v2..v6 (`enrich_v2_*` etc.) and legacy `enrich_*` entries are never looked up — they expire.
  // v7 (S1, 2026-08-13): cancels now carry pre_call_setter_ids/names, so the
  // cached SHAPE changed. Same key, same TTL => a v6 blob cannot self-
  // invalidate; only the prefix move evicts it. Surgical on purpose — a global
  // MANUAL_SCHEMA_VERSION bump would evict every altara_* cache app-wide for
  // what is otherwise a chip re-style. Precedent: S61 v2->v3, S82 v3->v4,
  // S83 v4->v5, S87 v5->v6.
  // v8 (2026-09-18): show-confirmation feature removed — the `confirmations`
  // field is gone from the cached enrichment SHAPE. Prefix move evicts the v7
  // blobs that still carry it.
  return `enrich_v8_${orgId}_${startISO}_${endISO}`;
};

/**
 * Invalidate all calendar cache entries for a given org.
 * Call this on org switch to prevent cross-tenant data.
 */
export const invalidateCalendarCache = (orgId: string): void => {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        try {
          const raw = localStorage.getItem(key);
          if (raw) {
            const entry: CacheEntry<unknown> = JSON.parse(raw);
            if (entry.orgId === orgId) {
              localStorage.removeItem(key);
            }
          }
        } catch {
          // Corrupted entry, remove it
          localStorage.removeItem(key!);
        }
      }
    }
  } catch {
    // Silent failure
  }
};
